import { useState } from "react";
import { Play, ChevronLeft, ChevronRight, TrendingDown, MessageCircle, Percent } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SLIDES = [
  {
    icon: TrendingDown, emoji: "📉", title: "Multi-Supplier Price Comparison",
    desc: "Compare prices across your own wholesalers and auto-route every case to the cheapest wholesaler — stores save £150+ a week.",
    from: "#1D4ED8", to: "#3B82F6",
  },
  {
    icon: MessageCircle, emoji: "💬", title: "Instant WhatsApp Depot List",
    desc: "Build a Cash & Carry pick list on the go and share it with your drivers or staff instantly over WhatsApp.",
    from: "#059669", to: "#10B981",
  },
  {
    icon: Percent, emoji: "📊", title: "POR Margin & Barcode Scanner",
    desc: "Track 30%+ Profit-on-Return margins and scan any barcode with continuous auto-focus for instant price and offer alerts.",
    from: "#7C3AED", to: "#9333EA",
  },
];

export const AboutModal = ({ open, onOpenChange }) => {
  const [i, setI] = useState(0);
  const s = SLIDES[i];
  const Icon = s.icon;
  const go = (n) => setI((prev) => (prev + n + SLIDES.length) % SLIDES.length);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setI(0); }}>
      <DialogContent data-testid="about-modal" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl bg-[#1D4ED8] flex items-center justify-center text-white text-sm font-extrabold">Z</span>
            About ZapBasket & How It Works
          </DialogTitle>
          <DialogDescription>A quick look at how ZapBasket helps your store buy smarter and protect margin.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Infographic slider */}
          <div className="relative">
            <div data-testid={`about-slide-${i}`} className="rounded-2xl p-6 text-white text-center shadow-lg min-h-[220px] flex flex-col items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${s.from} 0%, ${s.to} 100%)` }}>
              <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-3">
                <Icon className="w-8 h-8" strokeWidth={2.25} />
              </div>
              <p className="text-2xl mb-1">{s.emoji}</p>
              <p className="text-lg font-extrabold font-head leading-tight px-2">{s.title}</p>
              <p className="text-sm text-white/90 mt-2 px-1 leading-snug">{s.desc}</p>
            </div>
            <button data-testid="about-prev" onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 shadow flex items-center justify-center active:scale-90"><ChevronLeft className="w-5 h-5 text-slate-700" /></button>
            <button data-testid="about-next" onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 shadow flex items-center justify-center active:scale-90"><ChevronRight className="w-5 h-5 text-slate-700" /></button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2">
            {SLIDES.map((_, idx) => (
              <button key={idx} data-testid={`about-dot-${idx}`} onClick={() => setI(idx)}
                className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-[#1D4ED8]" : "w-2 bg-slate-300"}`} />
            ))}
          </div>

          {/* Non-blocking placeholder demo button */}
          <Button data-testid="about-watch-demo" onClick={() => toast.info("Demo video coming soon — message us on WhatsApp for a live tour.")}
            variant="outline" className="w-full h-12 font-bold gap-2">
            <Play className="w-4 h-4" /> Watch Demo Video (Coming Soon)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
